import { z } from 'zod'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { RouteRepository } from '../../application/RouteRepository'
import { classifyRouteMutationError, RouteMutationError } from '../../domain/routePlanning'

type PlanningOperations = Pick<
  RouteRepository,
  | 'getPlanningSnapshot'
  | 'ensureSeasonRoutes'
  | 'createRoute'
  | 'updateRoute'
  | 'previewPlacement'
  | 'placeLocations'
  | 'removeLocation'
  | 'setLocationPosition'
  | 'getMapSettings'
  | 'setMapSettings'
  | 'getFlagWalkState'
  | 'verifyFlag'
  | 'undoFlagVerification'
  | 'setFlagWalkProgress'
>

interface RpcResponse {
  data: unknown
  error: { code?: string; message?: string } | null
}

const nullableNumber = z
  .union([z.number(), z.string()])
  .nullable()
  .transform((value) => {
    if (value === null) return null
    const parsed = typeof value === 'number' ? value : Number(value)
    return Number.isFinite(parsed) ? parsed : null
  })

const routeSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  color: z.string(),
  description: z.string().nullable().default(null),
  season: z.string().nullable(),
  displayOrder: z.number().int().nullable(),
  version: z.number().int(),
  memberCount: z.number().int(),
  // Ajoutés par 20261023010000 : absents tant que la migration n'est pas poussée.
  updatedAt: z.string().nullable().default(null),
  updatedByName: z.string().nullable().default(null),
})

const flagVerificationSchema = z.object({
  id: z.uuid(),
  method: z.enum(['MOVED', 'CONFIRMED']),
  verifiedAt: z.string(),
  byName: z.string().nullable(),
  restored: z.boolean(),
})

const flagWalkStateSchema = z.object({
  route: z.object({
    id: z.uuid(),
    name: z.string(),
    color: z.string(),
    season: z.string().nullable(),
    version: z.number().int(),
  }),
  members: z.array(
    z.object({
      id: z.uuid(),
      position: z.number().int(),
      clientName: z.string().nullable(),
      addressLine1: z.string().nullable(),
      addressLine2: z.string().nullable(),
      city: z.string().nullable(),
      latitude: nullableNumber,
      longitude: nullableNumber,
      flagLatitude: nullableNumber,
      flagLongitude: nullableNumber,
      flagSource: z.string().nullable(),
      flagVersion: z.number().int(),
      verification: flagVerificationSchema.nullable(),
      addressLatitude: nullableNumber,
      addressLongitude: nullableNumber,
      captureRadiusM: nullableNumber,
    }),
  ),
  progress: z
    .object({ currentLocationId: z.uuid().nullable(), todoOnly: z.boolean(), updatedAt: z.string() })
    .nullable(),
  lastAction: z
    .object({
      verificationId: z.uuid(),
      locationId: z.uuid(),
      method: z.enum(['MOVED', 'CONFIRMED']),
      flagChanged: z.boolean(),
      verifiedAt: z.string(),
      undoable: z.boolean(),
    })
    .nullable(),
  defaultCaptureRadiusM: nullableNumber.pipe(z.number()),
  profileWorkRadiusM: nullableNumber,
})

const verifyFlagResultSchema = z.object({
  status: z.literal('VERIFIED'),
  locationId: z.uuid(),
  verification: z.object({
    id: z.uuid(),
    method: z.enum(['MOVED', 'CONFIRMED']),
    flagVersion: z.number().int(),
    flagChanged: z.boolean(),
  }),
  flag: z.object({
    latitude: nullableNumber.pipe(z.number()),
    longitude: nullableNumber.pipe(z.number()),
    source: z.string().nullable(),
    version: z.number().int(),
    captureRadiusM: nullableNumber,
  }),
})

const undoFlagResultSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('UNDONE'),
    verificationId: z.uuid(),
    locationId: z.uuid(),
    flagRestored: z.boolean(),
  }),
  z.object({
    status: z.literal('CONFLICT'),
    reason: z.string(),
    verificationId: z.uuid(),
    locationId: z.uuid(),
  }),
])

const locationSchema = z.object({
  id: z.uuid(),
  contractId: z.uuid(),
  clientPropertyId: z.uuid().nullable(),
  clientName: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  postalCode: z.string().nullable(),
  latitude: nullableNumber,
  longitude: nullableNumber,
  adjusted: z.boolean(),
  positionVersion: z.number().int(),
  // Ajoutés par 20261027010000 : absents tant que la migration n'est pas poussée.
  flagLatitude: nullableNumber.default(null),
  flagLongitude: nullableNumber.default(null),
  flagSource: z.string().nullable().default(null),
  // Ajoutés par 20261031010000 (ajustement maison par maison) : absents tant que la migration n'est pas poussée.
  flagVersion: z.number().int().default(0),
  flagVerification: flagVerificationSchema.nullable().default(null),
  // Ajouté par 20261031030000 (projection v2) : absent de la projection v1.
  flagCaptureRadiusM: nullableNumber.default(null),
  routeId: z.uuid().nullable(),
  position: z.number().int().nullable(),
})

const snapshotSchema = z.object({
  season: z.string(),
  seasons: z.array(z.string()),
  routes: z.array(routeSchema),
  locations: z.array(locationSchema),
  totals: z.object({
    eligible: z.number().int(),
    unassigned: z.number().int(),
    unlocated: z.number().int(),
    unassignedUnlocated: z.number().int(),
  }),
  recentIneligibleRemovals: z.array(
    z.object({
      serviceLocationId: z.uuid().nullable(),
      routeId: z.uuid(),
      addressLine1: z.string().nullable(),
      addressLine2: z.string().nullable(),
      occurredAt: z.string(),
    }),
  ),
  // Projection v2 (20261031030000) ; absent de la v1 : l'écran retombe sur le repli de 7 m.
  defaultCaptureRadiusM: nullableNumber.default(null),
})

const refusalSchema = z.object({
  locationId: z.uuid(),
  reason: z.enum(['NOT_FOUND', 'OTHER_SEASON', 'INELIGIBLE']),
})

const placeResultSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('APPLIED'),
    routeId: z.uuid(),
    placed: z.number().int(),
    movedFromOtherRoutes: z.number().int(),
    alreadyInRoute: z.number().int(),
    reordered: z.boolean(),
    routeVersions: z.record(z.string(), z.number().int()),
  }),
  z.object({ status: z.literal('REJECTED'), refused: z.array(refusalSchema) }),
])

const removeResultSchema = z.object({
  status: z.literal('APPLIED'),
  routeId: z.uuid(),
  routeVersion: z.number().int(),
  undo: z.object({
    routeId: z.uuid(),
    locationId: z.uuid(),
    afterLocationId: z.uuid().nullable(),
    beforeLocationId: z.uuid().nullable(),
  }),
})

const previewSchema = z.object({
  routeId: z.uuid(),
  unassigned: z.number().int(),
  alreadyInRoute: z.number().int(),
  movedFrom: z.array(z.object({ routeId: z.uuid(), count: z.number().int() })),
  refused: z.array(refusalSchema),
})

const positionResultSchema = z.object({
  locationId: z.uuid(),
  latitude: nullableNumber.pipe(z.number()),
  longitude: nullableNumber.pipe(z.number()),
  adjusted: z.boolean(),
  version: z.number().int(),
  previous: z.object({ adjustedLatitude: nullableNumber, adjustedLongitude: nullableNumber }),
})

const mapSettingsSchema = z.object({ detectionRadiusMeters: nullableNumber.pipe(z.number()) })

const routeIdResultSchema = z.object({ status: z.literal('APPLIED'), routeId: z.uuid() })

/** Fonction RPC inconnue de la base (migration pas encore poussée). */
function isMissingFunction(error: RpcResponse['error']): boolean {
  return error?.code === 'PGRST202' || error?.code === '42883'
}

export function createSupabaseRoutePlanning(client: SupabaseClient): PlanningOperations {
  async function call(name: string, command: object): Promise<RpcResponse> {
    try {
      return (await client.rpc(name, { p_command: command })) as RpcResponse
    } catch {
      // Réseau coupé avant toute réponse : issue inconnue.
      return { data: null, error: { message: 'NETWORK' } }
    }
  }

  async function read<T>(name: string, command: object, schema: z.ZodType<T>, failure: string): Promise<T> {
    const response = await call(name, command)
    if (response.error) {
      const message = response.error.message ?? ''
      throw new Error(message !== '' ? message : `${failure}:${response.error.code ?? 'UNKNOWN'}`)
    }
    const parsed = schema.safeParse(response.data)
    if (!parsed.success) throw new Error(`${failure}_RESPONSE_INVALID`)
    return parsed.data
  }

  async function write<T>(name: string, command: object, schema: z.ZodType<T>): Promise<T> {
    const response = await call(name, command)
    if (response.error) throw classifyRouteMutationError(response.error)
    const parsed = schema.safeParse(response.data)
    // Écriture peut-être appliquée mais réponse illisible : traitée comme incertaine (relecture).
    if (!parsed.success) throw new RouteMutationError('uncertain', 'ROUTE_RESPONSE_INVALID')
    return parsed.data
  }

  return {
    async getPlanningSnapshot(season) {
      // Projection v2 (rayon de capture) ; tant que 20261031030000 n'est pas poussée, la v1 reste lue : l'éditeur
      // ne doit jamais tomber pour une fonction absente.
      const v2 = await call('route_planning_snapshot_v2', { season })
      const response = isMissingFunction(v2.error) ? await call('route_planning_snapshot_v1', { season }) : v2
      if (response.error) {
        const message = response.error.message ?? ''
        throw new Error(
          message !== '' ? message : `ROUTE_PLANNING_SNAPSHOT_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      const parsed = snapshotSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('ROUTE_PLANNING_SNAPSHOT_FAILED_RESPONSE_INVALID')
      const snapshot = parsed.data
      return { ...snapshot, meta: { source: 'supabase', retrievedAt: new Date().toISOString() } }
    },
    async ensureSeasonRoutes(season) {
      const result = await write(
        'ensure_season_routes_v1',
        { season },
        z.object({ created: z.number().int() }),
      )
      return { created: result.created }
    },
    async createRoute(command) {
      const result = await write('create_route_v1', command, routeIdResultSchema)
      return { routeId: result.routeId }
    },
    async updateRoute(command) {
      const result = await write('update_route_v1', command, routeIdResultSchema)
      return { routeId: result.routeId }
    },
    previewPlacement(input) {
      return read('preview_route_placement_v1', input, previewSchema, 'ROUTE_PLACEMENT_PREVIEW_FAILED')
    },
    placeLocations(command) {
      return write('place_route_locations_v1', command, placeResultSchema)
    },
    async removeLocation(command) {
      const result = await write('remove_route_location_v1', command, removeResultSchema)
      return { routeId: result.routeId, routeVersion: result.routeVersion, undo: result.undo }
    },
    setLocationPosition(command) {
      return write('set_service_location_position_v1', command, positionResultSchema)
    },
    async getMapSettings() {
      let response: RpcResponse
      try {
        response = (await client.rpc('route_map_settings_v1')) as RpcResponse
      } catch {
        response = { data: null, error: { message: 'NETWORK' } }
      }
      if (response.error) throw new Error(response.error.message ?? 'ROUTE_MAP_SETTINGS_FAILED')
      const parsed = mapSettingsSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('ROUTE_MAP_SETTINGS_RESPONSE_INVALID')
      return parsed.data
    },
    setMapSettings(settings) {
      return write('set_route_map_settings_v1', settings, mapSettingsSchema)
    },
    getFlagWalkState(routeId) {
      return read('flag_walk_state_v2', { routeId }, flagWalkStateSchema, 'FLAG_WALK_STATE_FAILED')
    },
    verifyFlag(command) {
      return write('verify_service_location_flag_v2', command, verifyFlagResultSchema)
    },
    undoFlagVerification(command) {
      return write('undo_flag_verification_v1', command, undoFlagResultSchema)
    },
    async setFlagWalkProgress(command) {
      await write('set_flag_walk_progress_v1', command, z.object({ todoOnly: z.boolean() }))
    },
  }
}
